<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  room: { type: Object, required: true },
  children: { type: Array, default: () => [] },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits([
  'edit',
  'delete',
  'duplicate',
  'add-child',
  'edit-child',
  'delete-child',
]);

const { t } = useI18n();
const isExpanded = ref(false);

const hasChildren = computed(() => props.children.length > 0);
const activeChildrenCount = computed(
  () => props.children.filter(c => c.status === 'active').length
);

const statusLabels = {
  active: 'Ativo',
  inactive: 'Inativo',
  sold: 'Vendido',
  rented: 'Locado',
};

const statusColors = {
  active:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  inactive: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  sold: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  rented: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <div
    class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
  >
    <!-- Header do Card (Imóvel Principal) -->
    <div class="px-4 py-3">
      <!-- Linha 1: Tipo + Nome + Status + Ref -->
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <span
            v-if="room.room_type"
            class="shrink-0 text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
          >
            {{ room.room_type }}
          </span>
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate"
          >
            {{ room.name }}
          </h4>
          <span v-if="room.room_number" class="shrink-0 text-xs text-slate-500">
            #{{ room.room_number }}
          </span>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <span
            class="text-xs px-2 py-0.5 rounded-full"
            :class="statusColors[room.status] || statusColors.active"
          >
            {{ statusLabels[room.status] || 'Ativo' }}
          </span>
          <span
            v-if="room.external_ref"
            class="text-xs font-mono text-slate-400"
          >
            {{ room.external_ref }}
          </span>
        </div>
      </div>

      <!-- Linha 2: Slogan + Info -->
      <div
        v-if="room.slogan || room.max_occupancy"
        class="flex items-center justify-between mt-1"
      >
        <p
          v-if="room.slogan"
          class="text-xs text-slate-500 dark:text-slate-400 italic truncate"
        >
          {{ room.slogan }}
        </p>
        <div class="flex items-center gap-3 text-xs text-slate-500 shrink-0">
          <span
            v-if="hasChildren"
            class="font-medium"
            :class="
              activeChildrenCount > 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-slate-400'
            "
          >
            {{ activeChildrenCount }}/{{ children.length }} un.
          </span>
          <span v-else>{{ room.total_units || 1 }} un.</span>
          <span>{{ room.max_occupancy }} hosp.</span>
        </div>
      </div>

      <!-- Linha 3: Ações + Toggle Unidades -->
      <div class="flex items-center justify-between mt-2">
        <div class="flex items-center gap-2">
          <button
            class="text-xs text-woot-500 hover:text-woot-700 font-medium"
            @click="emit('edit', room)"
          >
            {{ t('MOTOR_RESERVA.COMMON.EDIT') }}
          </button>
          <button
            class="text-xs text-slate-500 hover:text-slate-700"
            :disabled="isSubmitting"
            @click="emit('duplicate', room)"
          >
            Duplicar
          </button>
          <button
            class="text-xs text-red-500 hover:text-red-700"
            @click="emit('delete', room)"
          >
            {{ t('MOTOR_RESERVA.COMMON.DELETE') }}
          </button>
        </div>

        <!-- Toggle Unidades -->
        <button
          v-if="hasChildren || !room.parent_id"
          class="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors"
          :class="
            hasChildren
              ? 'text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20'
              : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
          "
          @click="toggleExpanded"
        >
          <span
            class="w-4 h-4 transition-transform duration-200 i-lucide-chevron-down"
            :class="{ 'rotate-180': isExpanded }"
          />
          <template v-if="hasChildren">
            {{ children.length }} Unidade{{ children.length > 1 ? 's' : '' }}
          </template>
          <template v-else>Unidades</template>
        </button>
      </div>
    </div>

    <!-- Seção de Unidades Habitacionais (expandível) -->
    <div
      v-if="isExpanded"
      class="border-t border-slate-100 dark:border-slate-700"
    >
      <!-- Lista de UHs -->
      <div
        v-if="hasChildren"
        class="divide-y divide-slate-100 dark:divide-slate-700"
      >
        <div
          v-for="child in children"
          :key="child.id"
          class="flex items-center justify-between px-4 py-2 bg-slate-50/50 dark:bg-slate-900/30"
        >
          <div class="flex items-center gap-2 min-w-0 flex-1">
            <span class="text-slate-300 dark:text-slate-600 text-xs">└</span>
            <span
              v-if="child.room_type"
              class="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
            >
              {{ child.room_type }}
            </span>
            <span class="text-sm text-slate-700 dark:text-slate-200 truncate">
              {{ child.name }}
            </span>
            <span
              v-if="child.room_number"
              class="shrink-0 text-xs text-slate-400"
            >
              #{{ child.room_number }}
            </span>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span
              class="text-[10px] px-1.5 py-0.5 rounded-full"
              :class="statusColors[child.status] || statusColors.active"
            >
              {{ statusLabels[child.status] || 'Ativo' }}
            </span>
            <span
              v-if="child.external_ref"
              class="text-[10px] font-mono text-slate-400"
            >
              {{ child.external_ref }}
            </span>
            <button
              class="text-xs text-woot-500 hover:text-woot-700"
              @click="emit('edit-child', child)"
            >
              {{ t('MOTOR_RESERVA.COMMON.EDIT') }}
            </button>
            <button
              class="text-xs text-red-500 hover:text-red-700"
              @click="emit('delete-child', child)"
            >
              {{ t('MOTOR_RESERVA.COMMON.DELETE') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Mensagem vazia + Botão Nova Unidade -->
      <div class="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/30">
        <p v-if="!hasChildren" class="text-xs text-slate-400 mb-2">
          Nenhuma unidade vinculada.
        </p>
        <button
          class="flex items-center gap-1 text-xs font-medium text-woot-500 hover:text-woot-700"
          @click="emit('add-child', room)"
        >
          <span class="w-4 h-4 i-lucide-plus" />
          Nova Unidade
        </button>
      </div>
    </div>
  </div>
</template>
